import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Icon, Radio } from '@sendible/design-system';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { CalendarFloating, Container, DateContainer, DropdownPanel, OptionList, PresetRow, RadioGroupWrapper, Trigger } from './index.styles';
import { useReportingContext } from '../../context';
import { formatPeriodDisplay, getDateISO } from '../../helpers';

type DatePresetAction = 'last7Days' | 'last30Days' | 'last90Days';

interface DatePreset {
  text: string;
  action: DatePresetAction;
}

const getDateRangeFromAction = (action: DatePresetAction, timezoneOffset = 0): { startDate: string; endDate: string } => {
  const endDate = getDateISO(timezoneOffset, 1); // yesterday

  let daysAgo = 1;

  if (action === 'last7Days') {
    daysAgo = 7;
  } else if (action === 'last30Days') {
    daysAgo = 30;
  } else if (action === 'last90Days') {
    daysAgo = 90;
  }

  return {
    startDate: getDateISO(timezoneOffset, daysAgo),
    endDate,
  };
};

const PRESET_DAYS: Record<DatePresetAction, number> = {
  last7Days: 7,
  last30Days: 30,
  last90Days: 90,
};

const getActionFromDateRange = (startDate: string, endDate: string): DatePresetAction | null => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;

  const found = (Object.entries(PRESET_DAYS) as [DatePresetAction, number][]).find(([, days]) => days === diffDays);

  return found ? found[0] : null;
};

interface DateRangePickerProps {
  disabled?: boolean;
}

export const DateRangePicker = ({ disabled = false }: DateRangePickerProps) => {
  const { period, setPeriod } = useReportingContext();
  const { t } = useTranslation('reports');
  const [{ user }] = useBridgeContext();
  const timezoneOffset = user?.timezone ?? 0;

  const [isOpen, setIsOpen] = useState(false);
  const [isSelectingCustom, setIsSelectingCustom] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);
  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string }>(() =>
    getDateRangeFromAction('last30Days', timezoneOffset)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const { x, y, strategy, refs } = useFloating({
    open: isOpen || isSelectingCustom,
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const dateRangeItems: DatePreset[] = [
    { text: t('last_7'), action: 'last7Days' },
    { text: t('last_30'), action: 'last30Days' },
    { text: t('last_90'), action: 'last90Days' },
  ];

  const currentAction = getActionFromDateRange(period.startDate, period.endDate);
  const selectedPreset = dateRangeItems.find((item) => item.action === currentAction);
  const hasCustomRange = !selectedPreset && !!(period.startDate && period.endDate);
  const isCustomSelected = hasCustomRange;

  const getTriggerLabel = () => {
    if (isCustomSelected) {
      return period.startDate && period.endDate ? formatPeriodDisplay(period.startDate, period.endDate) : t('custom_range');
    }

    return selectedPreset?.text || t('last_30');
  };

  const triggerLabel = getTriggerLabel();

  const handlePresetSelect = useCallback(
    (action: DatePresetAction) => {
      setPeriod(getDateRangeFromAction(action, timezoneOffset));
      setIsOpen(false);
    },
    [setPeriod, timezoneOffset]
  );

  const handleCustomDateChange = useCallback(
    (startDate: string, endDate: string) => {
      setCustomDateRange({ startDate, endDate });
      setPeriod({ startDate, endDate });
      setIsSelectingCustom(false);
    },
    [setPeriod]
  );

  const handleSelectCustomMode = useCallback(() => {
    setIsOpen(false);
    setIsSelectingCustom(true);
    setCalendarKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isOpen && !isSelectingCustom) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const dropdownRoot = document.getElementById('dropdown-root');
      const inPanel = panelRef.current?.contains(target);
      const inDropdown = dropdownRoot?.contains(target);

      if (!inContainer && !inPanel && !inDropdown) {
        setIsOpen(false);
        setIsSelectingCustom(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setIsSelectingCustom(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isSelectingCustom]);

  const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

  return (
    <Container
      data-testid="date-range-picker"
      ref={(node) => {
        containerRef.current = node;
        refs.setReference(node); // Safely sets structural reference wrapper target avoiding child node interception
      }}
    >
      <Trigger
        data-testid="date-range-picker-trigger"
        data-open={isOpen}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsSelectingCustom(false);
            setIsOpen((prev) => !prev);
          }
        }}
        type="button"
      >
        <Icon
          color="brandDark"
          name="calendar"
        />
        <span className="trigger-label">{triggerLabel}</span>
      </Trigger>

      {isOpen &&
        dropdownRoot &&
        createPortal(
          <DropdownPanel
            data-testid="date-range-picker-dropdown"
            ref={(node) => {
              refs.setFloating(node);
              panelRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            <RadioGroupWrapper data-testid="date-range-picker-radio-group">
              <OptionList>
                {dateRangeItems.map(({ text, action }) => (
                  <li key={action}>
                    <PresetRow
                      data-testid={`date-range-picker-preset-${action}`}
                      onClick={() => handlePresetSelect(action)}
                    >
                      <Radio
                        groupName="date-range-preset"
                        isChecked={currentAction === action && !isCustomSelected}
                        label={text}
                        size="md"
                      />
                    </PresetRow>
                  </li>
                ))}
                <li>
                  <PresetRow
                    onClick={handleSelectCustomMode}
                    data-testid="date-range-picker-custom"
                  >
                    <Radio
                      data-testid="date-range-picker-preset-custom"
                      groupName="date-range-preset"
                      isChecked={isCustomSelected}
                      label=""
                      size="md"
                    />
                    <Icon
                      name="calendar"
                      color="brandDark"
                    />
                    <DateContainer>{formatPeriodDisplay(customDateRange.startDate, customDateRange.endDate)}</DateContainer>
                  </PresetRow>
                </li>
              </OptionList>
            </RadioGroupWrapper>
          </DropdownPanel>,
          dropdownRoot
        )}

      {isSelectingCustom &&
        dropdownRoot &&
        createPortal(
          <CalendarFloating
            ref={(node) => {
              refs.setFloating(node);
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            <DatePicker
              endDate={customDateRange.endDate}
              key={calendarKey}
              defaultOpen
              mode="past"
              onChange={handleCustomDateChange}
              placeholder={t('custom_range') as string}
              portalDomId="dropdown-root"
              startDate={customDateRange.startDate}
            />
          </CalendarFloating>,
          dropdownRoot
        )}
    </Container>
  );
};
